import React, { Component } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { Button } from '@zocdoc/components/src/button';
import { coral, green, grey25, grey75 } from '@zocdoc/branding/colors';
import { filterToValidAttributes } from '@zocdoc/dom-property';
import { ShowFor } from '@zocdoc/components/src/grid';
import { Alert, InvertedCheck } from '@zocdoc/components/src/icons';
import { Textbox } from '@zocdoc/components/src/textbox';
import { FontFamily } from '@zocdoc/components/src/typography';

import { createLinkData, sendBranchSms, SEND_SMS_RESULT } from './utils';
import { localizedStringType } from '../constants';

class SendSmsForm extends Component {
    static propTypes = {
        branchDeepLink: PropTypes.string,
        buttonText: PropTypes.shape(localizedStringType),
        failureText: PropTypes.shape(localizedStringType),
        smallFailureText: PropTypes.shape(localizedStringType),
        loadingText: PropTypes.shape(localizedStringType),
        onChangePhoneNumberInput: PropTypes.func.isRequired,
        onClickSendSms: PropTypes.func.isRequired,
        onViewSendSmsFailure: PropTypes.func.isRequired,
        onViewSendSmsSuccess: PropTypes.func.isRequired,
        smsText: PropTypes.shape(localizedStringType),
        successText: PropTypes.shape(localizedStringType),
        tags: PropTypes.arrayOf(PropTypes.string),
        title: PropTypes.shape(localizedStringType),
    };

    state = {
        hasTrackedTyping: false,
        phoneNumber: '',
        sendSmsResult: SEND_SMS_RESULT.none,
    };

    render() {
        const {
            buttonText,
            children,
        } = this.props;

        const { phoneNumber } = this.state;

        return (
            <form
                onSubmit={this.handleSubmitSendSms}
                {...filterToValidAttributes(this.props)}
            >
                <StyledTextbox
                    autoComplete="off"
                    className="exclude-from-playback"
                    data-test="tel-send-sms-input"
                    disabled={this.isLoadingBranchSms()}
                    onChange={this.handleInputChange}
                    placeholder="(xxx) xxx-xxxx"
                    type="text"
                    value={phoneNumber}
                />
                <StyledButton
                    disabled={this.isLoadingBranchSms()}
                    fluid
                >
                    <FormattedMessage {...buttonText} />
                </StyledButton>
                {children}
                {this.maybeRenderBranchResult()}
            </form>
        );
    }

    componentDidUpdate(prevProps, prevState) {
        const { onChangePhoneNumberInput } = this.props;

        const {
            phoneNumber,
            hasTrackedTyping,
        } = this.state;

        if (phoneNumber !== prevState.phoneNumber && !hasTrackedTyping) {
            onChangePhoneNumberInput();
            this.setState({ hasTrackedTyping: true });
        }
    }

    isLoadingBranchSms = () => {
        const { sendSmsResult } = this.state;
        return sendSmsResult === SEND_SMS_RESULT.loading;
    };

    handleInputChange = e => {
        this.setState({ phoneNumber: e.target.value });
    };

    handleSubmitSendSms = async e => {
        const {
            branchDeepLink,
            intl,
            onClickSendSms,
            onViewSendSmsFailure,
            onViewSendSmsSuccess,
            title,
            smsText,
            tags,
        } = this.props;
        const { phoneNumber } = this.state;

        e.preventDefault();
        this.setState({ sendSmsResult: SEND_SMS_RESULT.loading });
        onClickSendSms();

        try {
            const linkData = createLinkData({
                branchDeepLink,
                title: intl.formatMessage(title),
                smsText: intl.formatMessage(smsText),
                tags,
            });

            await sendBranchSms({
                linkData,
                phoneNumber,
            });
            this.setState({ sendSmsResult: SEND_SMS_RESULT.success });
            onViewSendSmsSuccess();
        } catch (err) {
            this.setState({ sendSmsResult: SEND_SMS_RESULT.failure });
            onViewSendSmsFailure();
        }
    };

    maybeRenderBranchResult() {
        const {
            failureText,
            smallFailureText,
            loadingText,
            successText,
        } = this.props;

        const { sendSmsResult } = this.state;

        switch (sendSmsResult) {
            case SEND_SMS_RESULT.success:
                return (
                    <BranchResult>
                        <BranchResultIcon>
                            <InvertedCheck color={green} />
                        </BranchResultIcon>
                        <BranchSuccessMessage>
                            <FormattedMessage {...successText} />
                        </BranchSuccessMessage>
                    </BranchResult>
                );
            case SEND_SMS_RESULT.failure:
                return (
                    <BranchResult>
                        <BranchResultIcon>
                            <Alert color={coral} />
                        </BranchResultIcon>
                        <BranchFailureMessage>
                            <ShowFor
                                small
                            >
                                <FormattedMessage {...smallFailureText} />
                            </ShowFor>
                            <ShowFor
                                large
                                medium
                                xlarge
                                xxlarge
                            >
                                <FormattedMessage {...failureText} />
                            </ShowFor>
                        </BranchFailureMessage>
                    </BranchResult>
                );
            case SEND_SMS_RESULT.loading:
                return (
                    <BranchResult>
                        <BranchLoadingMessage>
                            <FormattedMessage {...loadingText} />
                        </BranchLoadingMessage>
                    </BranchResult>
                );
            default:
                return null;
        }
    }
}

export default injectIntl(SendSmsForm);

export const StyledTextbox = styled(Textbox)`
    cursor: text;
    border: 1px solid ${grey25};
    padding: 11px 15px 11px 24px;
`;

export const StyledButton = styled(Button)`
    font: 18px/24px ${FontFamily.SharpSansSemibold};  
`;

export const BranchResult = styled.div`
    align-items: center;
    display: flex;
    justify-content: center;
    white-space: nowrap;
`;

const BranchResultIcon = styled.span`
    margin-right: 5px;
    vertical-align: middle;
`;

const BranchMessageBase = styled.span`
    font: 15px/24px ${FontFamily.SharpSansSemibold};
`;

const BranchLoadingMessage = styled(BranchMessageBase)`
    color: ${grey75};
`;

const BranchFailureMessage = styled(BranchMessageBase)`
    color: ${coral};
`;

const BranchSuccessMessage = styled(BranchMessageBase)`
    color: ${green};
`;
